import { useEffect, useRef, useState } from "react";
import { Slide } from "@progress/kendo-react-animation";
import { Notification } from "@progress/kendo-react-notification";

import ParagraphWithBoldParts from "components/ParagraphWithBoldParts";

import * as styles from "./styles.module.scss";

import type { ErrorDetails } from "models/ui";

const getRemainingTime = (time: number, duration: number) =>
  Math.max(time + duration - Date.now(), 0);

interface SnackProps {
  message: ErrorDetails;
  onHide: () => void;
  showTime: number;
}

const Snack = ({ message, onHide, showTime }: SnackProps) => {
  const [visible, setVisible] = useState(false);
  const progressAnimationRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message.time === 0) {
      setVisible(true);
    }

    if (message.time) {
      setVisible(getRemainingTime(message.time, showTime) > 0);
    }
  }, [message, showTime]);

  useEffect(() => {
    if (visible && message.time) {
      const updateProgress = () => {
        if (message.time && containerRef.current) {
          const elapsedSinceStart = Date.now() - message.time;
          const newProgress = elapsedSinceStart / showTime;
          containerRef.current.style.setProperty("--progress", newProgress.toString());
          if (newProgress <= 1) {
            progressAnimationRef.current = requestAnimationFrame(updateProgress);
          }
        }
      };

      progressAnimationRef.current = requestAnimationFrame(updateProgress);

      const timer = setTimeout(
        () => {
          setVisible(false);
        },
        getRemainingTime(message.time, showTime),
      );

      return () => {
        clearTimeout(timer);
        if (progressAnimationRef.current) {
          cancelAnimationFrame(progressAnimationRef.current);
        }
      };
    }

    return undefined;
  }, [visible, message, showTime]);

  return visible ?
      <div className={styles.snack} ref={containerRef}>
        <Slide direction={message.type ? "right" : "left"}>
          <Notification
            className={styles.notification}
            closable
            type={{ style: message.type ?? "error", icon: true }}
            onClose={onHide}
          >
            <div className={styles.title}>{message.type ?? "error"}!</div>
            <div className={styles.description} title={message.description}>
              <ParagraphWithBoldParts content={message.error ?? ""} />
            </div>
          </Notification>
        </Slide>
      </div>
    : null;
};

export default Snack;
